import { nanoid } from "nanoid";
import { isEmptyObject } from "@/utils";
import { pendingEventsStore } from "camera/store/pendingEvents";
import { useDevices } from "@/store/devices";
import { getIsPaired, getCanRecord } from "camera/store/station/selectors";
import { useStation } from "camera/store/station";
import { dataSyncEmitter } from "@/modules/events/emitter";
import CrashReporter from "@/modules/app/CrashReporter";
import { MediaUploadManager } from "../upload/MediaUploadManager";
import LocalEvents from "../LocalEvents";
import { MAXIMUM_EVENT_DURATION, MINIMUM_EVENT_DURATION } from "./constants";

const { addEvent: addEventToUpload, updateUnfinishedEvent } = pendingEventsStore();
const UPDATE_INTERVAL = 5000;

export default class EventManager {
  private static currentEvents: { [id: string]: CameraEvent } = {};
  private static lastActiveEvent: CameraEvent | null = null;
  private static lastManualVideoEvent: CameraEvent | null = null;
  private static durationUpdaters: { [id: string]: NodeJS.Timeout } = {};
  private static durationLimiters: { [id: string]: NodeJS.Timeout } = {};

  private static isPaired = (() => {
    const isPaired = getIsPaired(useStation.getState());
    useStation.subscribe((store: StationStore) => {
      this.isPaired = getIsPaired(store);
    });
    return isPaired;
  })();
  private static canRecord = (() => {
    const canRecord = getCanRecord(useStation.getState());
    useStation.subscribe((store: StationStore) => {
      this.canRecord = getCanRecord(store);
    });
    return canRecord;
  })();

  static setEndTimeOverride = (time: number | null) => {
    Object.values(this.currentEvents).forEach((event) => {
      if (time && time >= event.start && time <= event.end) {
        event.forcedEnd = time;
      }
    });
  };

  static getCurrentEvents = () => Object.values(this.currentEvents);
  static getLastActiveEvent = () => this.lastActiveEvent;
  static getLastManualVideoEvent = () => this.lastManualVideoEvent;

  static triggerEvent = (type: OneTimeEvent, jid: string) => {
    if (!this.isPaired) return;
    const device = useDevices.getState().devices[jid];
    const time = new Date().getTime();
    const currentEvent: CameraEvent = {
      uniqueId: nanoid(),
      type,
      finished: true,
      start: time,
      end: time,
      additionalData: { deviceName: device?.deviceName || "unknown" },
      intervalEvent: false
    };
    log.events("Triggered event", type, currentEvent.uniqueId);
    addEventToUpload(currentEvent);
    LocalEvents.save(currentEvent, { shouldAnnounce: true });
  };

  static startEvent = (type: RepeatedTimeEvent) => {
    if (!this.isPaired || !this.canRecord) {
      log.events("Not starting event, camera is either not paired or in OFF mode");
      return;
    }
    if (!MediaUploadManager.isRecording()) {
      // An event without any media would never finish processing on the server
      log.events("Not starting event, recorder is not recording");
      return;
    }
    const time = new Date().getTime();
    const currentEvent: CameraEvent = {
      uniqueId: nanoid(),
      type,
      finished: false,
      start: time,
      end: time + MINIMUM_EVENT_DURATION,
      additionalData: {},
      intervalEvent: true
    };
    this.checkForAndfinishBadEvent(currentEvent.type);

    log.events("Starting event", type, currentEvent.uniqueId, new Date(currentEvent.start).toLocaleTimeString());
    if (isEmptyObject(this.currentEvents)) MediaUploadManager.startMarkingDataForUpload();
    if (!this.currentEvents[currentEvent.uniqueId]) this.currentEvents[currentEvent.uniqueId] = currentEvent;

    addEventToUpload({ ...currentEvent });
    LocalEvents.save(currentEvent, { shouldAnnounce: true });
    this.createEventDurationUpdater(currentEvent.uniqueId);
    this.createEventDurationLimiter(currentEvent.uniqueId);
    if (type !== "MANUAL_VIDEO") this.lastActiveEvent = currentEvent;
    if (type === "MANUAL_VIDEO") this.lastManualVideoEvent = currentEvent;

    dataSyncEmitter.emit("event-state-change", currentEvent);
    return currentEvent;
  };

  static endEvent = (eventId: string) => {
    if (!this.isPaired) return;
    const currentEvent = this.currentEvents[eventId];
    if (!currentEvent) {
      log.warn("Trying to call 'endEvent' for event that does not exist", eventId);
      return;
    }
    log.events("Ending event", currentEvent.type, currentEvent.uniqueId);
    const now = new Date().getTime();
    this.deleteEventDurationUpdater(eventId);
    this.deleteEventDurationLimiter(eventId);

    if (currentEvent.forcedEnd) currentEvent.end = currentEvent.forcedEnd;
    else if (now > currentEvent.end) currentEvent.end = now;
    delete currentEvent.forcedEnd;

    currentEvent.finished = true;
    addEventToUpload({ ...currentEvent });
    LocalEvents.save(currentEvent, { shouldAnnounce: true });
    delete this.currentEvents[eventId];

    if (currentEvent.type !== "MANUAL_VIDEO") this.updateLastActiveEvent(currentEvent);
    if (currentEvent.type === "MANUAL_VIDEO") this.lastManualVideoEvent = currentEvent;

    dataSyncEmitter.emit("event-state-change", currentEvent);

    if (isEmptyObject(this.currentEvents)) MediaUploadManager.stopMarkingDataForUpload();
    return currentEvent;
  };

  private static createEventDurationUpdater = (id: string) => {
    if (this.durationUpdaters[id]) {
      log.warn(`Updater for event ${id} already exists`);
      return;
    }
    this.durationUpdaters[id] = setInterval(() => {
      this.currentEvents[id].end = new Date().getTime();
      updateUnfinishedEvent(this.currentEvents[id]);
    }, UPDATE_INTERVAL);
  };

  private static deleteEventDurationUpdater = (id: string) => {
    if (this.durationUpdaters[id]) {
      clearInterval(this.durationUpdaters[id]);
      delete this.durationUpdaters[id];
    } else log.warn("Trying to clear nonexisting updater", id);
  };

  private static createEventDurationLimiter = (id: string) => {
    if (this.durationLimiters[id]) {
      log.warn(`Limiter for event ${id} already exists`);
      return;
    }
    this.durationLimiters[id] = setTimeout(() => {
      dataSyncEmitter.emit("event-maximum-duration-reached", id);
      clearTimeout(this.durationLimiters[id]);
      delete this.durationLimiters[id];
    }, MAXIMUM_EVENT_DURATION);
  };

  private static deleteEventDurationLimiter = (id: string) => {
    if (this.durationLimiters[id]) {
      clearTimeout(this.durationLimiters[id]);
      delete this.durationLimiters[id];
    } else log.warn("Trying to clear nonexisting limiter", id);
  };

  private static checkForAndfinishBadEvent = (eventType: CameraEvent["type"]) => {
    const badEvent = Object.values(this.currentEvents).find((e) => e.type === eventType);
    if (badEvent) {
      CrashReporter.sendMessage(`Found bad ${eventType} event`);
      log.warn(
        `Found bad ${eventType} event, started at ${new Date(badEvent.start).toLocaleTimeString()}, will try to end ${
          badEvent.uniqueId
        }`
      );
      this.endEvent(badEvent.uniqueId);
    }
  };

  private static updateLastActiveEvent = (lastEvent: CameraEvent) => {
    const remainingEvents = Object.values(this.currentEvents);
    if (remainingEvents.length === 0) this.lastActiveEvent = lastEvent;
  };

  static reset = () => {
    this.currentEvents = {};
    Object.values(this.durationUpdaters).forEach((u) => clearInterval(u));
    Object.values(this.durationLimiters).forEach((u) => clearTimeout(u));
    this.durationUpdaters = {};
    this.durationLimiters = {};
    this.lastActiveEvent = null;
    this.lastManualVideoEvent = null;
  };
}
